import * as React from 'react';
import { formatCurrency, cn } from '@/lib/utils';
import { ArrowDown, ArrowUp, Plus } from 'lucide-react';
import type { Transaction } from '@/lib/types';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface RecentTransactionsProps {
  transactions: Transaction[];
  maxItems?: number;
}

const TransactionItem = React.memo(({ transaction }: { transaction: Transaction }) => {
  const isIncome = transaction.type === 'income';
  const date = new Date(transaction.date);

  return (
    <div className="flex items-center gap-3 py-2.5 group">
      {/* Icon */}
      <div className={cn(
        'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
        isIncome ? 'bg-emerald-50' : 'bg-rose-50'
      )}>
        {isIncome
          ? <ArrowUp className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
          : <ArrowDown className="w-4 h-4 text-rose-600" strokeWidth={2.5} />
        }
      </div>

      {/* Description + category */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">{transaction.description}</p>
        <p className="text-xs text-slate-400 truncate">
          {transaction.category || 'Uncategorized'} · {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </p>
      </div>

      {/* Amount */}
      <span className={cn(
        'text-sm font-bold flex-shrink-0',
        isIncome ? 'text-emerald-600' : 'text-slate-800'
      )}>
        {isIncome ? '+' : '-'}{formatCurrency(transaction.amount)}
      </span>
    </div>
  );
});

TransactionItem.displayName = 'TransactionItem';

export function RecentTransactions({ transactions, maxItems = 6 }: RecentTransactionsProps) {
  const recent = React.useMemo(() =>
    [...transactions]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, maxItems),
    [transactions, maxItems]
  );

  if (recent.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
          <Plus className="w-5 h-5 text-slate-400" />
        </div>
        <p className="text-sm font-semibold text-slate-700">No transactions yet</p>
        <p className="text-xs text-slate-400 mt-1">Add your first transaction to get started</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-50">
      {recent.map(t => <TransactionItem key={t.id} transaction={t} />)}
    </div>
  );
}
